import * as Y from 'yjs';
import * as awarenessProtocol from 'y-protocols/awareness';
import { type Message } from '@electric-sql/client';
import { base64ToUint8Array, uint8ArrayToBase64 } from '../../utils/yjs';
import { AwarenessBatcher } from './AwarenessBatcher';
import { ManagedShapeStream } from './ManagedShapeStream';
import { Logger } from '@/utils/logger';

const logger = Logger.getLoggerWithPrefix('ElectricYjsProvider')

export type SyncMode = 'solo' | 'collab';

export function parseElectricSeq(rawSeq: unknown): number | null {
  if (rawSeq === undefined || rawSeq === null) {
    return null;
  }

  if (typeof rawSeq === 'number') {
    return Number.isFinite(rawSeq) ? rawSeq : null;
  }

  if (typeof rawSeq === 'bigint') {
    return Number(rawSeq);
  }

  const normalized = String(rawSeq).replace(/n$/, '');
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : null;
}

export function shouldSkipElectricReplay(rawSeq: unknown, streamFloorSeq: number | null): boolean {
  const parsedSeq = parseElectricSeq(rawSeq);
  return parsedSeq !== null && streamFloorSeq !== null && parsedSeq <= streamFloorSeq;
}

export interface ElectricYjsProviderOptions {
  // Required for durable outbox scoping
  diagramId: string;
  doc: Y.Doc;
  awareness?: awarenessProtocol.Awareness;

  // Document updates configuration (via Electric)
  documentUpdates: {
    shapeUrl: string;
    sendUrl: string;
  };

  // Awareness configuration (via WebSocket to awareness service)
  awarenessUpdates?: {
    awarenessServiceUrl: string;
  };

  // Authentication
  authToken?: string;
  /** Rejects stale pre-migration updates at the Worker write boundary. */
  documentEpoch?: number;

  // Batching: debounce window for local Y.Doc updates (ms). 0 = immediate (no batching).
  batchWindowMs?: number;

  // When true, skip connecting to Electric SSE on construction (solo mode).
  // Call connectElectricNow() later to start the stream.
  deferElectric?: boolean;

  // Ignore replayed rows already covered by the bootstrap snapshot + catch-up load.
  streamFloorSeq?: number | null;

  // Callbacks
  onConnectionStateChange?: (state: 'connected' | 'disconnected') => void;
  onSyncStateChange?: (synced: boolean) => void;  // NEW: Signal when Electric is caught up
  onPendingCountChange?: (count: number) => void;
  // Fired in solo mode after a POST is confirmed — used for cross-tab broadcast
  onSoloConfirm?: (updateBase64: string, seq: number | null) => void;
  // Fired when a POST /updates fails (for surfacing errors in the UI)
  onSendError?: () => void;
  // Fired when the provider needs a local backup (tab hidden / before unload)
  onBackupNeeded?: (trigger: 'visibility_hidden' | 'before_unload') => void;
}

export class ElectricYjsProvider {
  private doc: Y.Doc;
  private awareness?: awarenessProtocol.Awareness;
  private documentStream?: ManagedShapeStream<any>;
  private ws?: WebSocket;
  private options: ElectricYjsProviderOptions;
  private isDestroyed = false;
  private updateHandler: ((update: Uint8Array, origin: any) => void) | null = null;
  private awarenessUpdateHandler: ((changes: any, origin: any) => void) | null = null;
  private awarenessBatcher?: AwarenessBatcher;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectTimeout?: number;
  private isConnectingStream = false; // Prevent concurrent connection attempts
  // Track pending local writes until acknowledged by Electric stream
  private pendingOps: Map<string, { seqStr?: string; updateBase64: string; createdAt: number } > = new Map();
  private pendingCounter = 0;
  private beforeUnloadHandler?: (e: BeforeUnloadEvent) => void;
  private pendingGcInterval?: number;
  private onlineHandler?: () => void;
  private visibilityHandler?: () => void;
  private drainTimer?: number;
  private backoffMs = 1000; // start at 1s, cap at 60s
  private latestSeq: number | null = null;
  private isDraining = false; // M2: re-entrancy guard for drainOutbox

  // Solo/collab sync mode
  private _syncMode: SyncMode = 'solo';
  latestConfirmedSeq: number | null = null;
  private streamFloorSeq: number | null = null;

  // Batching state
  private readonly batchWindowMs: number;
  private batchBuffer: Uint8Array[] = [];
  private batchTimer?: ReturnType<typeof setTimeout>;
  private periodicOutboxInterval?: number;
  private inFlightWrites = new Set<Promise<void>>();

  private updateLatestSeq(rawSeq: unknown) {
    const parsed = parseElectricSeq(rawSeq);
    if (parsed === null) {
      return;
    }

    if (this.latestSeq === null || parsed > this.latestSeq) {
      this.latestSeq = parsed;
    }
  }

  getLatestSeq(): number | null {
    return this.latestSeq;
  }

  get syncMode(): SyncMode {
    return this._syncMode;
  }

  get isElectricConnected(): boolean {
    return this.documentStream?.connected === true;
  }

  // Update latestConfirmedSeq from external source (e.g., cross-tab broadcast)
  updateConfirmedSeq(seq: number): void {
    if (this.latestConfirmedSeq === null || seq > this.latestConfirmedSeq) {
      this.latestConfirmedSeq = seq;
    }
  }

  setSyncMode(mode: SyncMode): void {
    if (this._syncMode === mode) return;
    logger.info(`Sync mode transition: ${this._syncMode} → ${mode}`);

    // When transitioning to collab, flush batch buffer first to ensure
    // all buffered edits are sent before Electric connects (§6.9)
    if (mode === 'collab') {
      this.flushBatch();
    }

    this._syncMode = mode;

    // When transitioning to collab, drain ops already confirmed by POST
    // but not yet echoed by Electric (they have a seqStr set)
    if (mode === 'collab') {
      let drained = false;
      for (const [id, p] of this.pendingOps.entries()) {
        if (p.seqStr) {
          this.pendingOps.delete(id);
          drained = true;
          (async () => {
            try {
              const { OutboxStorage } = await import('../persistence/OutboxStorage');
              await OutboxStorage.delete(id);
            } catch {}
          })();
        }
      }
      if (drained) this.notifyPendingCount();
    }
  }

  connectElectricNow(): void {
    if (this.isDestroyed) return;
    if (this.documentStream || this.isConnectingStream) {
      logger.warn('Electric already connected or connecting, skipping connectElectricNow');
      return;
    }
    logger.info('Lazy-connecting Electric SSE (collab mode)');
    this.connectDocumentStream();
  }

  private async notifyPendingCount() {
    try {
      const { OutboxStorage } = await import('../persistence/OutboxStorage');
      const outboxItems = await OutboxStorage.list(this.options.diagramId);
      const pendingIds = new Set(this.pendingOps.keys());
      const outboxOnlyCount = outboxItems.filter((item) => !pendingIds.has(item.id)).length;
      this.options.onPendingCountChange?.(this.pendingOps.size + outboxOnlyCount);
    } catch {
      // Fallback to pendingOps only
      try { this.options.onPendingCountChange?.(this.pendingOps.size); } catch {}
    }
  }

  constructor(options: ElectricYjsProviderOptions & { canWrite?: boolean }) {
    logger.info('🏗️  NEW PROVIDER INSTANCE CREATED', {
      canWrite: options.canWrite,
      stack: new Error().stack?.split('\n').slice(1, 4).join('\n')
    });
    
    this.doc = options.doc;
    this.awareness = options.awareness;
    this.options = options;
    this.batchWindowMs = options.batchWindowMs ?? 150;
    this._syncMode = options.deferElectric ? 'solo' : 'collab';
    this.streamFloorSeq = parseElectricSeq(options.streamFloorSeq);

    // Setup awareness batching
    if (this.awareness) {
      this.awarenessBatcher = new AwarenessBatcher(this.awareness);
    }

    // Start listening to local document changes ONLY if canWrite
    if (options.canWrite) {
      this.setupDocumentSync();
    }

    // Connect to Electric shape stream — or defer for solo mode
    if (options.deferElectric) {
      logger.info('Electric SSE deferred (solo mode)');
    } else {
      this.connectDocumentStream();
    }

    // Connect to WebSocket for awareness immediately
    logger.info('🎬 Constructor: Checking awareness config', {
      hasAwareness: !!this.awareness,
      hasAwarenessUpdates: !!this.options.awarenessUpdates
    });
    
    if (this.awareness && this.options.awarenessUpdates) {
      this.connectAwarenessWebSocket();
    } else {
      logger.warn('⚠️ Skipping awareness WebSocket connection', {
        hasAwareness: !!this.awareness,
        hasAwarenessUpdates: !!this.options.awarenessUpdates
      });
    }

    // Install beforeunload guard to prevent losing pending ops on reload
    this.beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      this.flushBatchSync();
      this.options.onBackupNeeded?.('before_unload');
      if (this.pendingOps.size > 0) {
        const msg = 'Operation in progress. Leaving may discard changes.';
        e.preventDefault();
        e.returnValue = msg;
        return msg;
      }
      return undefined as any;
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    // Resume/drain when back online or tab becomes visible
    this.onlineHandler = () => {
      this.documentStream?.resume();
      this.drainOutboxSoon(0);
    };
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.documentStream?.resume();
        this.drainOutboxSoon(0);
      } else if (document.visibilityState === 'hidden') {
        this.flushBatch();
        this.options.onBackupNeeded?.('visibility_hidden');
      }
    };
    window.addEventListener('online', this.onlineHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);

    // Try to drain immediately on startup if online
    if (typeof navigator === 'undefined' || navigator.onLine) {
      this.drainOutboxSoon(100);
    }

    // Periodically persist batch buffer to outbox (safety net every 5s)
    this.periodicOutboxInterval = window.setInterval(() => {
      this.persistBatchToOutbox();
    }, 5000);

    // GC for stale pending ops (2 min TTL) — needed even in solo mode
    this.pendingGcInterval = window.setInterval(() => {
      const now = Date.now();
      let removed = false;
      for (const [id, p] of this.pendingOps.entries()) {
        if (now - p.createdAt > 120_000) {
          logger.warn('Expiring stale pending op (TTL)', { id, ageMs: now - p.createdAt });
          this.pendingOps.delete(id);
          removed = true;
        }
      }
      if (removed) this.notifyPendingCount();
    }, 30_000);
  }

  private generateRequestId(): string {
    return (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
      ? (crypto as any).randomUUID()
      : `rid-${Date.now()}-${++this.pendingCounter}-${Math.random().toString(36).slice(2,8)}`;
  }

  private registerPending(clientRequestId: string, base64Update: string) {
    this.pendingOps.set(clientRequestId, { updateBase64: base64Update, createdAt: Date.now() });
  }

  private async clearConfirmedLocalWrite(clientRequestId: string) {
    this.pendingOps.delete(clientRequestId);
    try {
      const { OutboxStorage } = await import('../persistence/OutboxStorage');
      await OutboxStorage.delete(clientRequestId);
    } catch {}
    await this.notifyPendingCount();
  }

  private async sendUpdate(update: Uint8Array) {
    if (this.isDestroyed) return;
    try {
      const base64Update = uint8ArrayToBase64(update);
      const clientRequestId = this.generateRequestId();
      this.registerPending(clientRequestId, base64Update);
      try {
        const { OutboxStorage } = await import('../persistence/OutboxStorage');
        await OutboxStorage.put({
          id: clientRequestId,
          diagramId: this.options.diagramId,
          updateBase64: base64Update,
          createdAt: Date.now(),
          attempts: 0,
        });
      } catch (outboxErr) {
        logger.warn('Outbox write failed:', outboxErr);
      }
      await this.notifyPendingCount();

      const headers: HeadersInit = { 'Content-Type': 'application/octet-stream' };
      if (this.options.authToken) {
        headers['Authorization'] = `Bearer ${this.options.authToken}`;
      }
      headers['X-Client-Request-Id'] = clientRequestId;
      headers['X-Document-Epoch'] = String(this.options.documentEpoch ?? 0);

      logger.info('📤 POST /updates', { url: this.options.documentUpdates.sendUrl, updateSize: update.byteLength, clientRequestId });
      const response = await fetch(this.options.documentUpdates.sendUrl, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: update,
      });

      if (!response.ok) {
        logger.error('❌ Server rejected update:', response.status, response.statusText);
        this.options.onSendError?.();
        this.drainOutboxSoon(this.backoffMs);
      } else {
        try {
          const data = await response.json().catch(() => null);
          logger.info('✅ POST /updates response', { status: response.status, data });
          const rawSeq = (data as any)?.seq;
          const seqStr = rawSeq === undefined || rawSeq === null
            ? undefined
            : (typeof rawSeq === 'bigint' ? rawSeq.toString() : String(rawSeq).replace(/n$/, ''));
          if (seqStr !== undefined) {
            this.updateLatestSeq(seqStr);
            const pending = this.pendingOps.get(clientRequestId);
            if (pending) {
              pending.seqStr = seqStr;
              this.pendingOps.set(clientRequestId, pending);
            }
            try {
              const { OutboxStorage } = await import('../persistence/OutboxStorage');
              await OutboxStorage.setSeq(clientRequestId, seqStr);
            } catch {}
          }

          // POST success means the local write is durably stored. Electric echoes are
          // still used for remote replay, but the save indicator should not wait on them.
          await this.clearConfirmedLocalWrite(clientRequestId);

          // Solo mode (or collab with Electric not yet connected): notify cross-tab sync
          // from POST response. C3: handles mode transition mid-flight.
          if (this._syncMode === 'solo' || (this._syncMode === 'collab' && !this.isElectricConnected)) {
            // C2: Monotonic guard — never regress latestConfirmedSeq
            if (seqStr) {
              const parsed = parseInt(seqStr, 10);
              if (this.latestConfirmedSeq === null || parsed > this.latestConfirmedSeq) {
                this.latestConfirmedSeq = parsed;
              }
            }
            logger.info('📡 onSoloConfirm firing', { syncMode: this._syncMode, seq: seqStr, updateSize: base64Update.length, hasCallback: !!this.options.onSoloConfirm });
            this.options.onSoloConfirm?.(base64Update, seqStr ? parseInt(seqStr, 10) : null);
          } else {
            logger.info('⏭️ Skipping onSoloConfirm', { syncMode: this._syncMode, isElectricConnected: this.isElectricConnected });
          }
        } catch (err) {
          logger.warn('Unable to parse update ack JSON:', err);
        }
      }
    } catch (error) {
      logger.error('Failed to send document update:', error);
      this.options.onSendError?.();
      this.drainOutboxSoon(this.backoffMs);
    }
  }

  private queueUpdateWrite(update: Uint8Array): Promise<void> {
    const write = this.sendUpdate(update);
    this.inFlightWrites.add(write);
    void write.finally(() => {
      this.inFlightWrites.delete(write);
    });
    return write;
  }

  private setupDocumentSync() {
    this.updateHandler = (update: Uint8Array, origin: any) => {
      if (origin === 'electric' || origin === 'server' || origin === 'awareness' || origin === 'reset' || origin === 'broadcast' || this.isDestroyed) {
        return;
      }
      if (this.batchWindowMs <= 0) {
        void this.queueUpdateWrite(update);
        return;
      }
      this.batchBuffer.push(update);

      // Flush immediately if buffer exceeds 40KB to prevent data loss
      const totalBytes = this.batchBuffer.reduce((sum, u) => sum + u.byteLength, 0);
      if (totalBytes >= 40_000) {
        this.flushBatch();
        return;
      }

      if (this.batchTimer !== undefined) clearTimeout(this.batchTimer);
      this.batchTimer = setTimeout(() => {
        this.batchTimer = undefined;
        this.flushBatch();
      }, this.batchWindowMs);
    };

    this.doc.on('update', this.updateHandler);
  }

  private flushBatch(): Promise<void> | undefined {
    if (this.batchTimer !== undefined) {
      clearTimeout(this.batchTimer);
      this.batchTimer = undefined;
    }
    if (this.batchBuffer.length === 0) return undefined;

    const updates = this.batchBuffer;
    this.batchBuffer = [];

    // Delete the periodic outbox safety-net entry (fire-and-forget)
    (async () => {
      try {
        const { OutboxStorage } = await import('../persistence/OutboxStorage');
        await OutboxStorage.delete(`batch-buffer-${this.options.diagramId}`);
      } catch {}
    })();

    const merged = updates.length === 1 ? updates[0] : Y.mergeUpdates(updates);
    return this.queueUpdateWrite(merged);
  }

  private flushBatchSync() {
    if (this.batchTimer !== undefined) {
      clearTimeout(this.batchTimer);
      this.batchTimer = undefined;
    }
    if (this.batchBuffer.length === 0) return;

    const updates = this.batchBuffer;
    this.batchBuffer = [];

    const merged = updates.length === 1 ? updates[0] : Y.mergeUpdates(updates);
    const base64Update = uint8ArrayToBase64(merged);
    const clientRequestId = this.generateRequestId();

    this.registerPending(clientRequestId, base64Update);

    // Use fetch with keepalive to survive page teardown (binary)
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/octet-stream' };
      if (this.options.authToken) {
        headers['Authorization'] = `Bearer ${this.options.authToken}`;
      }
      headers['X-Client-Request-Id'] = clientRequestId;
      headers['X-Document-Epoch'] = String(this.options.documentEpoch ?? 0);

      fetch(this.options.documentUpdates.sendUrl, {
        method: 'POST',
        headers,
        credentials: 'include',
        keepalive: true,
        body: merged,
      });
    } catch {
      // Fallback to sendBeacon if keepalive fetch fails (e.g. payload too large)
      try {
        const payload = JSON.stringify({ update: base64Update, clientRequestId });
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(this.withDocumentEpoch(this.options.documentUpdates.sendUrl), blob);
      } catch {}
    }
  }

  private async persistBatchToOutbox() {
    if (this.batchBuffer.length === 0) return;
    try {
      const merged = this.batchBuffer.length === 1
        ? this.batchBuffer[0]
        : Y.mergeUpdates(this.batchBuffer);
      const base64 = uint8ArrayToBase64(merged);
      const { OutboxStorage } = await import('../persistence/OutboxStorage');
      await OutboxStorage.put({
        id: `batch-buffer-${this.options.diagramId}`,
        diagramId: this.options.diagramId,
        updateBase64: base64,
        createdAt: Date.now(),
        attempts: 0,
      });
    } catch (err) {
      logger.warn('Periodic outbox persist failed:', err);
    }
  }

  private withDocumentEpoch(url: string): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}documentEpoch=${encodeURIComponent(String(this.options.documentEpoch ?? 0))}`;
  }

  async flush(): Promise<void> {
    const flushedWrite = this.flushBatch();
    if (flushedWrite) {
      await flushedWrite;
    }

    // A write may already be in flight because the debounce window elapsed
    // immediately before export preparation began. Wait until every update
    // POST that existed during the flush has received its server response so
    // a following export job cannot hydrate the previous persisted state.
    while (this.inFlightWrites.size > 0) {
      await Promise.all([...this.inFlightWrites]);
    }
  }

  private connectAwarenessWebSocket() {
    if (!this.awareness || !this.options.awarenessUpdates) {
      logger.warn('Awareness not configured');
      return;
    }

    // Close existing WebSocket if any
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = undefined;
    }

    const wsUrl = this.options.awarenessUpdates.awarenessServiceUrl;
    logger.info('Connecting awareness WebSocket...');

    try {
      this.ws = new WebSocket(wsUrl);
      this.ws.binaryType = 'arraybuffer';

      this.ws.onopen = () => {
        logger.info('✅ Awareness connected');
        
        // Reset reconnect attempts on successful connection
        this.reconnectAttempts = 0;
        
        if (this.isDestroyed || !this.awareness) return;

        // Provide WS to batcher
        if (this.awarenessBatcher) {
          this.awarenessBatcher.setWebSocket(this.ws!);
        }

        // Send initial awareness state
        setTimeout(() => {
          if (this.ws && this.ws.readyState === WebSocket.OPEN && this.awareness) {
            const update = awarenessProtocol.encodeAwarenessUpdate(
              this.awareness,
              [this.awareness.clientID]
            );
            this.ws.send(update);
          }
        }, 100);
      };

      this.ws.onmessage = (event) => {
        if (this.isDestroyed || !this.awareness) return;

        try {
          const data = new Uint8Array(event.data);
          awarenessProtocol.applyAwarenessUpdate(this.awareness, data, 'websocket');
        } catch (error) {
          logger.error('Failed to apply awareness update:', error);
        }
      };

      this.ws.onerror = (err) => {
        logger.error('❌ Awareness WebSocket error:', err);
      };

      this.ws.onclose = (event) => {
        if (!event.wasClean) {
          logger.warn('⚠️ Awareness disconnected unexpectedly');
        }
        
        // Clear WS reference from batcher
        if (this.awarenessBatcher) {
          this.awarenessBatcher.setWebSocket(null);
        }
        
        // Attempt reconnection if not destroyed and not a clean close
        if (!this.isDestroyed && !event.wasClean) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      logger.error('Failed to connect awareness WebSocket:', error);
      logger.error('URL:', wsUrl);
    }
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.error('❌ Max reconnection attempts reached');
      return;
    }

    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s, 60s max
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 60000);
    this.reconnectAttempts++;

    logger.info(`🔄 Reconnecting in ${Math.round(delay/1000)}s (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimeout = window.setTimeout(() => {
      if (!this.isDestroyed) {
        this.connectAwarenessWebSocket();
      }
    }, delay);
  }

  private connectDocumentStream() {
    // Prevent concurrent connection attempts
    if (this.isConnectingStream) {
      logger.warn('⚠️ Already connecting to document stream, skipping...');
      return;
    }

    // Clean up existing document stream if any
    if (this.documentStream) {
      logger.warn('⚠️ Cleaning up EXISTING document stream - possible re-creation detected!');
      this.documentStream.disconnect();
      this.documentStream = undefined;
    }

    this.isConnectingStream = true;
    logger.info('🔌 Creating NEW ManagedShapeStream connection');
    
    try {
      // Create managed stream wrapper
      this.documentStream = new ManagedShapeStream({
        url: this.options.documentUpdates.shapeUrl,
        headers: this.options.authToken
          ? { 'Authorization': `Bearer ${this.options.authToken}` }
          : undefined,
      });

      logger.info('✅ ManagedShapeStream created, subscribing to messages...');

      let messageCount = 0;
      let lastMessageTime = Date.now();
      
      this.documentStream.subscribe((messages: Message<any>[]) => {
        if (this.isDestroyed) return;

        messageCount++;
        const now = Date.now();
        const timeSinceLastMessage = now - lastMessageTime;
        lastMessageTime = now;
        
        logger.info(`📨 Received message batch #${messageCount}, ${messages.length} messages (Δt=${timeSinceLastMessage}ms)`);

        for (const message of messages) {
          let parsedSeq: number | null = null;
          // Reconcile pending operations: prefer clientRequestId, then seq, then content/time
              try {
                const clientRequestId = (message as any).value?.client_request_id || (message as any).value?.clientRequestId;
                if (typeof clientRequestId === 'string' && this.pendingOps.has(clientRequestId)) {
                  this.pendingOps.delete(clientRequestId);
                  this.notifyPendingCount();
                  // Ensure outbox entry is removed as well
                  (async () => {
                    try {
                      const { OutboxStorage } = await import('../persistence/OutboxStorage');
                      await OutboxStorage.delete(clientRequestId);
                    } catch {}
                  })();
                }

                // Prefer seq from value, else attempt to extract from message.key (Electric shape PK)
                let seqAny = (message as any).value?.seq;
                if (seqAny === undefined || seqAny === null) {
                  const keyAny = (message as any).key;
                  if (keyAny !== undefined && keyAny !== null) {
                    if (typeof keyAny === 'object' && 'seq' in keyAny) {
                      seqAny = (keyAny as any).seq;
                    } else if (typeof keyAny === 'bigint' || typeof keyAny === 'number' || typeof keyAny === 'string') {
                      seqAny = keyAny as any;
                    }
                  }
                }
                parsedSeq = parseElectricSeq(seqAny);
                if (parsedSeq !== null) {
                  const seqStr = String(parsedSeq);
                  this.updateLatestSeq(seqStr);
                  for (const [id, p] of this.pendingOps.entries()) {
                    if (p.seqStr === seqStr) {
                      this.pendingOps.delete(id);
                      this.notifyPendingCount();
                      (async () => {
                        try {
                          const { OutboxStorage } = await import('../persistence/OutboxStorage');
                          await OutboxStorage.delete(id);
                        } catch {}
                      })();
                      break;
                    }
                  }
                } else {
                  const msgBase64 = (message as any).value?.update_bytes as string | undefined;
                  if (msgBase64) {
                    for (const [id, p] of this.pendingOps.entries()) {
                      if (p.updateBase64 === msgBase64) {
                        this.pendingOps.delete(id);
                        this.notifyPendingCount();
                        (async () => {
                          try {
                            const { OutboxStorage } = await import('../persistence/OutboxStorage');
                            await OutboxStorage.delete(id);
                            await OutboxStorage.deleteByContent(this.options.diagramId, msgBase64);
                          } catch {}
                        })();
                        break;
                      }
                    }
                  }
                }
              } catch (reconcileErr) {
                logger.warn('Pending-ops reconcile failed:', reconcileErr);
              }
          // Check for control messages (up-to-date signal)
          if (message.headers?.control) {
            if (message.headers.control === 'up-to-date') {
              logger.info('🎯 Electric shape stream is UP-TO-DATE');
              if (this.options.onSyncStateChange) {
                this.options.onSyncStateChange(true);
              }
            }
            continue;
          }

          if (shouldSkipElectricReplay(parsedSeq, this.streamFloorSeq)) {
            logger.info('Skipping replayed Electric update', {
              seq: parsedSeq,
              streamFloorSeq: this.streamFloorSeq,
            });
            continue;
          }

          // Process any message with a value (Electric doesn't use action field)
          if ('value' in message && message.value?.update_bytes) {
            try {
              const updateBytes = base64ToUint8Array(message.value.update_bytes);
              Y.applyUpdate(this.doc, updateBytes, 'electric');
              logger.info('✅ Applied update from Electric');


            } catch (error) {
              logger.error('Failed to apply document update:', error);
            }
          }
        }
      });
      
      this.isConnectingStream = false;
    } catch (error) {
      this.isConnectingStream = false;
      logger.error('Failed to connect to document stream:', error);
    }
  }

  private drainOutboxSoon(delayMs: number) {
    const d = Math.min(Math.max(delayMs, 0), 60_000);
    if (this.drainTimer) window.clearTimeout(this.drainTimer);
    this.drainTimer = window.setTimeout(() => this.drainOutbox(), d);
  }

  private async drainOutbox() {
    if (this.isDestroyed || this.isDraining) return;
    this.isDraining = true;
    try {
      const { OutboxStorage } = await import('../persistence/OutboxStorage');
      if (this.isDestroyed) return;
      const items = await OutboxStorage.list(this.options.diagramId);
      if (items.length === 0) {
        this.backoffMs = 1000;
        await this.notifyPendingCount();
        return;
      }

      // Skip items already being handled by sendUpdate (already in pendingOps)
      // M6: Also skip items that were already confirmed (have seqStr) from prior sessions
      const itemsToSend = items.filter(item =>
        !this.pendingOps.has(item.id) && !(item as any).seqStr
      );
      if (itemsToSend.length === 0) {
        this.backoffMs = 1000;
        await this.notifyPendingCount();
        return;
      }

      // Merge all outbox items into a single Yjs update
      const updates = itemsToSend.map(item => base64ToUint8Array(item.updateBase64));
      const merged = updates.length === 1 ? updates[0] : Y.mergeUpdates(updates);
      const mergedBase64 = uint8ArrayToBase64(merged);

      const batchRequestId = this.generateRequestId();
      this.registerPending(batchRequestId, mergedBase64);
      await this.notifyPendingCount();

      try {
        const headers: HeadersInit = { 'Content-Type': 'application/octet-stream' };
        if (this.options.authToken) {
          headers['Authorization'] = `Bearer ${this.options.authToken}`;
        }
        headers['X-Client-Request-Id'] = batchRequestId;
        headers['X-Document-Epoch'] = String(this.options.documentEpoch ?? 0);

        const response = await fetch(this.options.documentUpdates.sendUrl, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: merged,
        });

        if (!response.ok) {
          this.backoffMs = Math.min(this.backoffMs * 2, 60_000);
          this.drainOutboxSoon(this.backoffMs);
          await this.notifyPendingCount();
          return;
        }

        const data = await response.json().catch(() => ({}));
        const rawSeq = (data as any)?.seq;
        const seqStr = rawSeq === undefined || rawSeq === null
          ? undefined
          : (typeof rawSeq === 'bigint' ? rawSeq.toString() : String(rawSeq).replace(/n$/, ''));

        if (seqStr) {
          this.updateLatestSeq(seqStr);
          const pending = this.pendingOps.get(batchRequestId);
          if (pending) {
            pending.seqStr = seqStr;
            this.pendingOps.set(batchRequestId, pending);
          }
        }

        // Solo mode (or collab with Electric not yet connected): update latestConfirmedSeq
        if (this._syncMode === 'solo' || (this._syncMode === 'collab' && !this.isElectricConnected)) {
          if (seqStr) {
            const parsed = parseInt(seqStr, 10);
            if (this.latestConfirmedSeq === null || parsed > this.latestConfirmedSeq) {
              this.latestConfirmedSeq = parsed;
            }
          }
          this.options.onSoloConfirm?.(mergedBase64, seqStr ? parseInt(seqStr, 10) : null);
        }

        // Clear all original outbox items and their pendingOps entries
        for (const item of itemsToSend) {
          await OutboxStorage.delete(item.id);
          this.pendingOps.delete(item.id);
        }
        this.pendingOps.delete(batchRequestId);

        this.backoffMs = 1000;
        await this.notifyPendingCount();
      } catch {
        this.backoffMs = Math.min(this.backoffMs * 2, 60_000);
        this.drainOutboxSoon(this.backoffMs);
        await this.notifyPendingCount();
      }
    } catch {
      this.backoffMs = Math.min(this.backoffMs * 2, 60_000);
      this.drainOutboxSoon(this.backoffMs);
    } finally {
      this.isDraining = false;
    }
  }

  
  destroy() {
    this.isDestroyed = true;

    // Flush any buffered updates using sync path (keepalive fetch)
    // isDestroyed set first to prevent async callbacks from running
    this.flushBatchSync();

    // Clear batch state
    if (this.batchTimer !== undefined) {
      clearTimeout(this.batchTimer);
      this.batchTimer = undefined;
    }
    if (this.periodicOutboxInterval !== undefined) {
      clearInterval(this.periodicOutboxInterval);
      this.periodicOutboxInterval = undefined;
    }
    this.batchBuffer = [];

    // Clear reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
    }

    // Remove beforeunload listener
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = undefined;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = undefined;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = undefined;
    }
    if (this.drainTimer) {
      window.clearTimeout(this.drainTimer);
      this.drainTimer = undefined;
    }

    if (this.updateHandler) {
      this.doc.off('update', this.updateHandler);
      this.updateHandler = null;
    }

    if (this.awarenessUpdateHandler && this.awareness) {
      this.awareness.off('update', this.awarenessUpdateHandler);
      this.awarenessUpdateHandler = null;
    }

    if (this.awarenessBatcher) {
      this.awarenessBatcher.destroy();
      this.awarenessBatcher = undefined;
    }

    if (this.documentStream) {
      this.documentStream.disconnect();
    }

    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = undefined;
    }

    // Clear pending ops (skip notifyPendingCount — isDestroyed, no callbacks needed)
    this.pendingOps.clear();
    if (this.pendingGcInterval) {
      clearInterval(this.pendingGcInterval);
      this.pendingGcInterval = undefined;
    }
  }
}
